import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { fetchAdminAppSession } from '../api/adminAppSessionApi';
import type {
  AdminAppSessionContractUserDto,
  AdminRole,
  UseAppSessionResult,
} from '../types/adminSession.api';

function normalizeAdminRole(value: unknown): AdminRole | null {
  if (value === 'client' || value === 'admin' || value === 'superadmin') {
    return value;
  }

  return null;
}

function normalizeCurrentUser(user: AdminAppSessionContractUserDto | null | undefined) {
  if (!user) {
    return null;
  }

  return {
    id: user.id ?? null,
    name: user.name ?? null,
    email: user.email ?? null,
    role: normalizeAdminRole(user.roles?.[0]),
    hasWorkspaceAccess:
      typeof user.hasWorkspaceAccess === 'boolean'
        ? user.hasWorkspaceAccess
        : null,
  };
}

type SharedAppSessionState = Omit<UseAppSessionResult, 'refetch'>;

const INITIAL_SHARED_STATE: SharedAppSessionState = {
  data: null,
  error: null,
  isPending: true,
  isRefetching: false,
};

let sharedState: SharedAppSessionState = INITIAL_SHARED_STATE;
let sharedRequest: Promise<void> | null = null;
let sharedRequestVersion = 0;
let mountedConsumerCount = 0;
const sharedListeners = new Set<() => void>();

function emitSharedStateChange() {
  sharedListeners.forEach((listener) => listener());
}

function setSharedState(
  nextState:
    | SharedAppSessionState
    | ((currentState: SharedAppSessionState) => SharedAppSessionState),
) {
  sharedState =
    typeof nextState === 'function' ? nextState(sharedState) : nextState;
  emitSharedStateChange();
}

function subscribeToSharedState(listener: () => void) {
  sharedListeners.add(listener);

  return () => {
    sharedListeners.delete(listener);
  };
}

function getSharedStateSnapshot(): SharedAppSessionState {
  return sharedState;
}

function resetSharedSessionState() {
  sharedRequestVersion += 1;
  sharedRequest = null;
  sharedState = INITIAL_SHARED_STATE;
}

function hasSharedSessionSnapshot(): boolean {
  return (
    sharedState !== INITIAL_SHARED_STATE ||
    sharedState.data !== null ||
    sharedState.error !== null ||
    sharedState.isPending !== INITIAL_SHARED_STATE.isPending ||
    sharedState.isRefetching !== INITIAL_SHARED_STATE.isRefetching
  );
}

async function loadSharedSession(mode: 'initial' | 'refetch'): Promise<void> {
  if (sharedRequest) {
    return sharedRequest;
  }

  const requestVersion = sharedRequestVersion + 1;
  sharedRequestVersion = requestVersion;

  setSharedState((current) => ({
    data: mode === 'initial' ? null : current.data,
    error: null,
    isPending: mode === 'initial',
    isRefetching: mode === 'refetch',
  }));

  sharedRequest = (async () => {
    try {
      const response = await fetchAdminAppSession();
      const user = normalizeCurrentUser(response.data?.currentUser);

      if (requestVersion !== sharedRequestVersion) {
        return;
      }

      setSharedState({
        data: user ? { user } : null,
        error: null,
        isPending: false,
        isRefetching: false,
      });
    } catch (error) {
      if (requestVersion !== sharedRequestVersion) {
        return;
      }

      setSharedState((current) => ({
        data: mode === 'initial' ? null : current.data,
        error:
          error instanceof Error ? error : new Error('Session bridge unavailable.'),
        isPending: false,
        isRefetching: false,
      }));
    } finally {
      if (requestVersion === sharedRequestVersion) {
        sharedRequest = null;
      }
    }
  })();

  return sharedRequest;
}

export function useAppSession(): UseAppSessionResult {
  const state = useSyncExternalStore(
    subscribeToSharedState,
    getSharedStateSnapshot,
    getSharedStateSnapshot,
  );

  useEffect(() => {
    mountedConsumerCount += 1;

    if (mountedConsumerCount === 1 && !hasSharedSessionSnapshot()) {
      void loadSharedSession('initial');
    }

    return () => {
      mountedConsumerCount = Math.max(0, mountedConsumerCount - 1);
    };
  }, []);

  const refetch = useCallback(async () => {
    await loadSharedSession('refetch');
  }, []);

  return useMemo(
    () => ({
      ...state,
      refetch,
    }),
    [refetch, state],
  );
}

export function resetAppSessionStateForTests() {
  mountedConsumerCount = 0;
  resetSharedSessionState();
}
